import { type System, ComponentStore, type World } from '@ecs/world';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import type {
	CameraResource,
	InputResource,
	KinematicComponent,
	PlatformComponent,
	PlayerState,
	StageBounds,
	TimeScaleResource,
	TransformComponent,
	VelocityComponent
} from '@game/scenes/characterPrototype/types';
import { clamp } from '@game/scenes/characterPrototype/math';

export type ApplyTimeBoostFn = (world: World, value: number, duration: number) => void;

export type MovementSystemOptions = {
	boundsKey: string;
	inputKey: string;
	gameOverKey: string;
	gameOverReasonKey: string;
	playerColliderHeightScale: number;
	applyTimeBoost: ApplyTimeBoostFn;
};

export function createMovementSystem(
	kinematics: ComponentStore<KinematicComponent>,
	transforms: ComponentStore<TransformComponent>,
	velocities: ComponentStore<VelocityComponent>,
	states: ComponentStore<PlayerState>,
	platforms: ComponentStore<PlatformComponent>,
	options: MovementSystemOptions
): System {
	return ({ delta, world }) => {
		const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
		const bounds = world.getResource<StageBounds>(options.boundsKey);
		const input = world.getResource<InputResource>(options.inputKey);
		const timescale = world.getResource<TimeScaleResource>('timescale') ?? ({ value: 1 } as TimeScaleResource);
		const dt = delta * timescale.value;
		const camera = world.getResource<CameraResource>('camera');
		const isGameOver = world.getResource<boolean>(options.gameOverKey) ?? false;

		transforms.forEach((transform, entity) => {
			const velocity = velocities.get(entity);
			if (!velocity) return;
			const kinematic = kinematics.get(entity);
			if (!kinematic) return;
			const state = states.get(entity);
			if (!state) return;

			// Keep player kinematic values in sync (hot-tunable)
			if (entity === world.getResource<number>('player-entity')) {
				kinematic.speed = tuning.playerSpeed;
				kinematic.halfSize = tuning.playerSize / 2;
				kinematic.halfHeight = (tuning.playerSize / 2) * options.playerColliderHeightScale;
			}

			const prevY = transform.y;
			const magnitude = input ? Math.min(Math.hypot(input.x, input.y), 1) : 0;
			const dirX = magnitude > 0 ? input!.x / magnitude : 0;
			const dirY = magnitude > 0 ? input!.y / magnitude : 0;
			const moveScale = magnitude > 0 ? Math.max(tuning.moveMinScale, magnitude) : 0; // boost low-input mobility
			if (state.skipJumpLock > 0) {
				state.skipJumpLock = Math.max(0, state.skipJumpLock - dt);
			}
			if (state.wallRunLock > 0) {
				state.wallRunLock = Math.max(0, state.wallRunLock - dt);
			}
			if (state.dashCooldown > 0) {
				state.dashCooldown = Math.max(0, state.dashCooldown - dt);
			}
			const dashWasActive = tuning.dashEnabled && state.dashTime > 0;
			if (state.dashTime > 0) {
				state.dashTime = Math.max(0, state.dashTime - dt);
			}
			const dashActiveNow = tuning.dashEnabled && state.dashTime > 0;
			// Celeste-like: when dash ends, don't keep "inertia" from the dash velocity.
			if (dashWasActive && !dashActiveNow) {
				velocity.x = 0;
				velocity.y = 0;
			}
			if (state.dropThroughTimer > 0) {
				state.dropThroughTimer = Math.max(0, state.dropThroughTimer - dt);
			}
			if (!Number.isFinite(state.dropHoldTimer)) state.dropHoldTimer = 0;

			const dashActive = tuning.dashEnabled && state.dashTime > 0;

			// Dash overrides normal control/physics for a short duration.
			if (dashActive) {
				const dashSpeed = tuning.dashSpeed;
				velocity.x = state.dashDirX * dashSpeed;
				velocity.y = state.dashDirY * dashSpeed;
			} else {
				// Horizontal control
				velocity.x = dirX * kinematic.speed * moveScale;
			}

			// Ledge pull-in: smoothly climb to target height without auto-jumping
			if (!dashActive && state.climbingLedge) {
				// If user disables ledge grab while climbing, immediately drop back to normal physics.
				if (!tuning.ledgeGrabEnabled) {
					state.climbingLedge = false;
				} else {
					const targetY = state.ledgeTargetY;
					if (!Number.isFinite(targetY)) {
						state.climbingLedge = false;
						// fall back to normal movement/physics next tick
					}

					const pullSpeed = Math.max(0, tuning.ledgePullSpeed);
					const climbStepRaw = pullSpeed * dt;
					// Safety: never allow a "stuck climb" (would lock jumping + keep time boost active).
					// If climb speed is effectively zero (due to tuning or dt), snap to target and finish.
					if (!Number.isFinite(climbStepRaw) || climbStepRaw <= 0.0001) {
						transform.y = targetY;
						state.climbingLedge = false;
						state.grounded = true;
						state.canJump = true;
						state.jumpCharge = 0;
						state.skipJumpLock = tuning.ledgeJumpLock;
						velocity.y = 0;
						return;
					}

					options.applyTimeBoost(world, tuning.timeBoostLedgeValue, tuning.timeBoostLedgeDuration);
					const climbDir = Math.sign(targetY - transform.y);
					const climbStep = climbStepRaw;
					const deltaY = Math.min(Math.abs(targetY - transform.y), climbStep) * climbDir;

					transform.x += velocity.x * dt;
					transform.y += deltaY;
					velocity.y = 0;
					state.grounded = false;

					if (bounds) {
						transform.x = clamp(
							transform.x,
							kinematic.halfSize,
							Math.max(kinematic.halfSize, bounds.width - kinematic.halfSize)
						);
					}

					const reached = Math.abs(targetY - transform.y) <= 1;
					if (reached) {
						transform.y = targetY;
						state.climbingLedge = false;
						state.grounded = true;
						state.canJump = true;
						state.jumpCharge = 0;
						state.skipJumpLock = tuning.ledgeJumpLock;
						velocity.y = 0;
					}

					if (camera && transform.y < camera.y + camera.followThreshold) {
						camera.y = transform.y - camera.followThreshold;
					}
					return;
				}
			}

			// Wall-run: while airborne, pushing into a wall keeps you on it and slows fall / allows climb
			if (!dashActive && tuning.wallRunEnabled && bounds) {
				const leftWallX = kinematic.halfSize;
				const rightWallX = bounds.width - kinematic.halfSize;
				const nearLeft = transform.x <= leftWallX + tuning.wallRunMargin;
				const nearRight = transform.x >= rightWallX - tuning.wallRunMargin;
				const wantsLeft = dirX < -tuning.wallRunInputXMin;
				const wantsRight = dirX > tuning.wallRunInputXMin;
				const touchingWallSide: -1 | 1 | null = nearLeft && wantsLeft ? -1 : nearRight && wantsRight ? 1 : null;
				const nearWallSide: -1 | 1 | null = nearLeft ? -1 : nearRight ? 1 : null;

				if (state.wallRunning) {
					const stillTouching =
						state.wallSide === -1
							? nearLeft && wantsLeft
							: state.wallSide === 1
								? nearRight && wantsRight
								: false;
					const ascent = state.wallRunStartY - transform.y;
					const reachedLimit = state.wallRunTime >= tuning.wallRunMaxTime || ascent >= tuning.wallRunMaxAscent;
					if (!stillTouching || reachedLimit) {
						state.wallRunning = false;
						state.wallRunTime = 0;
						state.wallRunLock = tuning.wallRunRegrabLock;
						if (reachedLimit) {
							state.wallRunExhausted = true;
						}
					} else {
						state.wallRunTime += dt;
						transform.x = state.wallSide === -1 ? leftWallX : rightWallX;

						// while holding up, add upward accel; otherwise clamp fall speed for "slide"
						if (dirY < tuning.wallRunUpInputThreshold) {
							velocity.y -= tuning.wallRunUpAccel * dt;
						} else if (velocity.y > tuning.wallRunMaxFallSpeed) {
							velocity.y = tuning.wallRunMaxFallSpeed;
						}

						// wall jump (use same jump zone checks, but allow from wall even when airborne)
						const inJumpZone = dirY < tuning.jumpInputThreshold && magnitude >= tuning.jumpMagThreshold;
						const pushingAway = dirX * state.wallSide < -tuning.wallJumpInputAwayMin;
						if (state.skipJumpLock <= 0 && inJumpZone && pushingAway) {
							velocity.y = -tuning.jumpForce * tuning.wallJumpYScale;
							velocity.x = -state.wallSide * tuning.wallJumpXSpeed;
							state.wallRunning = false;
							state.wallRunTime = 0;
							state.wallRunLock = tuning.wallRunRegrabLock;
							state.wallRunExhausted = false;
						}
					}
				} else if (!state.grounded && state.wallRunLock <= 0 && touchingWallSide && !state.wallRunExhausted) {
					state.wallRunning = true;
					state.wallSide = touchingWallSide;
					state.wallRunTime = 0;
					state.wallRunStartY = transform.y;
					transform.x = touchingWallSide === -1 ? leftWallX : rightWallX;
					if (velocity.y > tuning.wallRunMaxFallSpeed) {
						velocity.y = tuning.wallRunMaxFallSpeed;
					}
				} else if (!state.grounded && state.wallRunExhausted && nearWallSide) {
					// After reaching the wall-run limit, only a wall-jump (push away + jump) can reset it.
					const inJumpZone = dirY < tuning.jumpInputThreshold && magnitude >= tuning.jumpMagThreshold;
					const pushingAway = dirX * nearWallSide < -tuning.wallJumpInputAwayMin;
					if (state.skipJumpLock <= 0 && inJumpZone && pushingAway) {
						velocity.y = -tuning.jumpForce * tuning.wallJumpYScale;
						velocity.x = -nearWallSide * tuning.wallJumpXSpeed;
						state.wallRunLock = tuning.wallRunRegrabLock;
						state.wallRunExhausted = false;
					}
				}
			} else {
				state.wallRunning = false;
				state.wallRunTime = 0;
			}

			// Immediate jump with hold-to-boost while stick is kept in jump zone
			const inJumpZone = dirY < tuning.jumpInputThreshold && magnitude >= tuning.jumpMagThreshold;

			// Drop through platform (only when grounded): briefly ignore platform collisions so player can fall down.
			const dropThreshold = clamp(tuning.platformDropInputThreshold, 0, 1);
			const dropEps = 1e-3;
			let hasUpThrustRemaining = false;
			if (!dashActive) {
				const wantsPlatformDrop =
					tuning.platformDropEnabled &&
					state.grounded &&
					magnitude + dropEps >= dropThreshold &&
					dirY >= 0.35 &&
					!inJumpZone &&
					state.skipJumpLock <= 0;
				if (wantsPlatformDrop) {
					state.dropHoldTimer += dt;
				} else {
					state.dropHoldTimer = 0;
				}
				if (wantsPlatformDrop && state.dropHoldTimer >= tuning.platformDropHoldDuration) {
					state.dropHoldTimer = 0;
					state.dropThroughTimer = tuning.platformDropIgnoreDuration;
					state.grounded = false;
					state.groundedPlatform = null;
					state.canJump = false;
					state.jumpCharge = tuning.jumpChargeMax;
					velocity.y = Math.max(velocity.y, tuning.platformDropKickSpeed);
				}

				if (state.grounded && state.canJump && state.skipJumpLock <= 0 && inJumpZone) {
					velocity.y = -tuning.jumpForce * tuning.jumpInitialScale;
					state.canJump = false;
					state.grounded = false;
					state.jumpCharge = 0;
				} else if (!state.grounded && !state.canJump && inJumpZone && state.jumpCharge < tuning.jumpChargeMax) {
					const remaining = tuning.jumpChargeMax - state.jumpCharge;
					const applied = Math.min(remaining, dt);
					state.jumpCharge += applied;
					velocity.y -= tuning.jumpHoldForce * applied;
				} else if (!inJumpZone && !state.grounded) {
					state.jumpCharge = tuning.jumpChargeMax;
				}
				hasUpThrustRemaining =
					!state.grounded && !state.canJump && inJumpZone && state.jumpCharge < tuning.jumpChargeMax;
			} else {
				// No hold-jump / platform drop stacking while dashing.
				state.dropHoldTimer = 0;
				state.jumpCharge = tuning.jumpChargeMax;
			}

			const gravityScale = state.wallRunning ? tuning.wallRunGravityScale : 1;

			// Stick down: accelerate falling toward nearest platform below (if any).
			if (tuning.downDropEnabled && input && !dashActive) {
				const down = clamp(
					(dirY - tuning.downDropInputThreshold) / Math.max(1e-6, 1 - tuning.downDropInputThreshold),
					0,
					1
				);
				if (down > 0 && !state.grounded && !state.climbingLedge) {
					let bestDist = Number.POSITIVE_INFINITY;
					platforms.forEach((platform) => {
						const platformY = platform.y;
						const feetY = transform.y + (kinematic.halfHeight ?? kinematic.halfSize);
						if (platformY < feetY) return;
						// must be horizontally reachable
						const overlapsX =
							transform.x >= platform.x - kinematic.halfSize &&
							transform.x <= platform.x + platform.width + kinematic.halfSize;
						if (!overlapsX) return;
						const dist = platformY - feetY;
						if (dist < bestDist) bestDist = dist;
					});

					if (Number.isFinite(bestDist) && bestDist < Number.POSITIVE_INFINITY) {
						const ttp = Math.max(0.05, tuning.downDropTimeToPlatform);
						const desiredVy = Math.min(tuning.downDropMaxSpeed, bestDist / ttp);
						velocity.y = Math.max(velocity.y, desiredVy * down);
					} else {
						// No platform directly below in X: just fall faster.
						velocity.y += tuning.gravity * dt * (tuning.downDropGravityScale - 1) * down;
					}
				}
			}

			if (!dashActive) {
				velocity.y += tuning.gravity * dt * gravityScale;
			} else {
				// Make sure nothing modified dash vertical velocity this tick.
				velocity.y = state.dashDirY * tuning.dashSpeed;
			}
			transform.x += velocity.x * dt;
			transform.y += velocity.y * dt;

			if (!bounds) return;
			transform.x = clamp(
				transform.x,
				kinematic.halfSize,
				Math.max(kinematic.halfSize, bounds.width - kinematic.halfSize)
			);

			const wasGrounded = state.grounded;
			const prevGroundedPlatform = state.groundedPlatform;
			state.grounded = false;
			let landedOnPlatform: number | null = null;
			let landed = false;
			platforms.forEach((platform, platformEntity) => {
				if (landed) return;
				if (state.dropThroughTimer > 0) return;
				const platformBottom = platform.y + platform.height;
				if (
					prevY + (kinematic.halfHeight ?? kinematic.halfSize) <= platform.y &&
					transform.y + (kinematic.halfHeight ?? kinematic.halfSize) >= platform.y &&
					velocity.y >= 0 &&
					transform.x >= platform.x - kinematic.halfSize &&
					transform.x <= platform.x + platform.width + kinematic.halfSize
				) {
					transform.y = platform.y - (kinematic.halfHeight ?? kinematic.halfSize);
					velocity.y = 0;
					state.grounded = true;
					state.climbingLedge = false;
					state.ledgeTargetY = transform.y;
					state.wallRunning = false;
					state.wallRunTime = 0;
					landedOnPlatform = platformEntity;
					landed = true;
				} else if (
					!state.grounded &&
					// Ledge grab only at the "end of jump": do not allow while jump-hold thrust is still available.
					tuning.ledgeGrabEnabled &&
					!hasUpThrustRemaining &&
					// Also avoid grabbing while moving upward too fast (prevents magnet grabbing during strong ascent).
					velocity.y >= -tuning.ledgeGrabMaxUpSpeed &&
					platformBottom - (transform.y - (kinematic.halfHeight ?? kinematic.halfSize)) >= 0 &&
					platformBottom - (transform.y - (kinematic.halfHeight ?? kinematic.halfSize)) <= tuning.ledgeGrabMargin &&
					transform.x >= platform.x - kinematic.halfSize &&
					transform.x <= platform.x + platform.width + kinematic.halfSize
				) {
					state.climbingLedge = true;
					state.canJump = false;
					state.jumpCharge = 0;
					state.ledgeTargetY = platform.y - (kinematic.halfHeight ?? kinematic.halfSize);
					velocity.y = 0;
					state.wallRunning = false;
					state.wallRunTime = 0;
					landed = true;
				}
			});

			// Reset "hold to drop" only when you actually change grounded surface / leave ground.
			if (state.grounded) {
				state.groundedPlatform = landedOnPlatform;
				if (!wasGrounded || prevGroundedPlatform !== landedOnPlatform) {
					state.dropHoldTimer = 0;
				}
			} else {
				state.groundedPlatform = null;
				state.dropHoldTimer = 0;
			}

			if (state.grounded) {
				state.canJump = true;
				state.jumpCharge = 0;
				state.wallRunExhausted = false;
				state.wallRunTime = 0;
			}

			if (camera && bounds) {
				// Normal mode: upward-follow.
				if (transform.y < camera.y + camera.followThreshold) {
					camera.y = transform.y - camera.followThreshold;
				}
				camera.peakY = Math.min(camera.peakY, camera.y);

				// Normal mode: limited down-follow (prevents player going behind joystick/HUD).
				if (!isGameOver && tuning.cameraDownFollowEnabled) {
					const downThreshold = clamp(tuning.cameraDownFollowThreshold, 0, bounds.height - 1);
					const desiredCamY = transform.y - (bounds.height - downThreshold);
					if (desiredCamY > camera.y) {
						const maxDown = Math.max(0, tuning.cameraDownFollowMax);
						camera.y = Math.min(desiredCamY, camera.peakY + maxDown);
					}
				}
				// Game over: allow camera to follow down as the player falls.
				if (isGameOver) {
					const downThreshold = bounds.height - camera.followThreshold;
					if (transform.y > camera.y + downThreshold) {
						camera.y = transform.y - downThreshold;
					}
				} else {
					// Trigger game over when player falls below the visible screen (after limited down-follow clamp).
					if (transform.y > camera.y + bounds.height + (kinematic.halfHeight ?? kinematic.halfSize)) {
						world.setResource<'fell' | 'dead'>(options.gameOverReasonKey, 'fell');
						world.setResource<boolean>(options.gameOverKey, true);
						// Pull camera down immediately so player doesn't disappear.
						const downThreshold = bounds.height - camera.followThreshold;
						camera.y = transform.y - downThreshold;
					}
				}
			}
		});
	};
}


